import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useI18n } from "@/i18n/I18nProvider";
import FeedbackEmoj from "./feedBackEmoj";
import { arFont, enFont } from "@/styles/font";
import FeedbackSlider from "./FeedBackSlider";
import { submitFeedback } from "./submitFeedback";
import { toast } from "react-toastify";
import { styles } from "../form";
import { colors } from "@/utils/const";

const FeedBack = ({ onClose }) => {
  const { t, locale } = useI18n();

  const [feedbackData, setFeedbackData] = useState({
    satisfactionRating: 0,
    informationHelpful: null,
    recommendationRating: 0,
    comments: "",
  });

  const [loading, setLoading] = useState(false);
  const isFormValid =
    feedbackData.satisfactionRating !== 0 &&
    feedbackData.informationHelpful !== null &&
    feedbackData.recommendationRating !== 0;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const result = await submitFeedback(feedbackData);

      if (result?.status === 200) {
        toast.success(t.feedback.successFeedback, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });

        // reset form
        setFeedbackData({
          satisfactionRating: 0,
          informationHelpful: null,
          recommendationRating: 0,
          comments: "",
        });

        onClose();
      } else {
        toast.error(t.feedback.failFeedback, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      toast.error(t.feedback.failFeedback, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        borderRadius="16px"
        data-aos="zoom-in"
        className={locale == "en" ? enFont.className : arFont.className}
        sx={{
          position: "fixed",
          top: {
            xs: "60px",
            sm: "90px",
            md: "30px",
          },
          [locale === "en" ? "left" : "right"]: {
            xs: "29px",
            sm: "40px",
          },
          width: {
            xs: "80%",
            sm: "333px",
          },
          backgroundColor: "#fff",
          boxShadow: 5,
          zIndex: 10000,
          fontFamily: "inherit",
          height: "auto",
        }}
      >
        {/* Background and banner*/}
        <Box
          sx={{
            width: "100%",
            height: "169px",
            backgroundColor: "#16289F",
            backgroundImage: "url('https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/feedbackbanner.webp')",
            borderTopRightRadius: "16px",
            borderTopLeftRadius: "16px",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        ></Box>
        {/* close button*/}
        <Box
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            top: "10px",
            [locale === "en" ? "right" : "left"]: "10px",
            backgroundColor: "rgba(255,255,255,0.2)",
            cursor: "pointer",
            transition: "background 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.4)",
            },
          }}
          onClick={() => onClose()}
        >
          <img
            src="https://contact-app-prod.s3.us-east-2.amazonaws.com/contact.eg/feedback/Close-Circle.svg"
            alt={locale === "ar" ? "إغلاق" : "Close"}
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </Box>
        <Box
          sx={{
            px: "20px",
            backgroundColor: "#fff",
          }}
        >
          {t.feedback.sections.map((section) => (
            <FeedbackEmoj
              key={section.id}
              question={section.question}
              options={section.options || section.defaultOptions}
              value={
                section.type === "satisfactionRating"
                  ? (section.options || section.defaultOptions).find(
                      (opt) => opt.text === feedbackData[section.type],
                    )?.id || feedbackData[section.type]
                  : feedbackData[section.type]
              }
              onSelect={(val) => {
                let newValue;

                if (section.type === "informationHelpful") {
                  newValue = val === 1;
                } else if (section.type === "satisfactionRating") {
                  const selectedOption = (
                    section.options || section.defaultOptions
                  ).find((opt) => opt.id === val);
                  newValue = selectedOption ? selectedOption.text : val;
                } else {
                  newValue = val;
                }

                const updated = {
                  ...feedbackData,
                  [section.type]: newValue,
                };

                setFeedbackData(updated);
              }}
            />
          ))}



          {/* Rate slider Section*/}
          <FeedbackSlider
            config={t.feedback.slider}
            t={t}
            rate={feedbackData.recommendationRating}
            onChange={(val) =>
              setFeedbackData({ ...feedbackData, recommendationRating: val })
            }
          />

          {/* TextArea Section*/}
          <Box
            sx={{
              width: "100%",
              height: "87px",
              borderBottomRightRadius: "16px",
              borderBottomLeftRadius: "16px",
              backgroundColor: "#fff",
              boxSizing: "border-box",
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <textarea
              placeholder={t.feedback?.textareaPlaceholder}
              value={feedbackData.comments}
              onChange={(e) =>
                setFeedbackData({ ...feedbackData, comments: e.target.value })
              }
              style={{
                width: "100%",
                height: "100%",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #9AA4B2",
                direction: locale === "ar" ? "rtl" : "ltr",
                fontSize: "12px",
                resize: "none",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </Box>
          {/* Submit button Section*/}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 0 5px 0",
              my: 2,
            }}
          >
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
              sx={{ ...styles.btn, fontSize: "16px", fontWeight: "500" }}
              style={{
                backgroundColor: isFormValid
                  ? colors.dark_blue
                  : colors.light_grey,
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff " }} />
              ) : (
                t.feedback?.buttonText
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FeedBack;
